import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useSiteConfig, DEFAULT_SEO_FOOTER } from "@/lib/siteConfig";

function RowLink({ link }) {
  const { url, label, target } = link;
  const external = /^https?:\/\//.test(url || "") || (url || "").startsWith("/api") || target === "_blank";
  const cls = "group flex items-center justify-between gap-3 py-3 border-t border-slate-100 text-slate-700 hover:text-primary transition-colors";
  const inner = (
    <>
      <span className="text-[15px]">{label}</span>
      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
    </>
  );
  if (external) {
    return (
      <a href={url} target={target || "_self"} rel={target === "_blank" ? "noopener noreferrer" : undefined}
        className={cls} data-testid={`seo-link-${link.id}`}>{inner}</a>
    );
  }
  return <Link to={url || "#"} className={cls} data-testid={`seo-link-${link.id}`}>{inner}</Link>;
}

function LocationCard({ col }) {
  const links = (col.links || []).filter((l) => l.enabled !== false && l.label && l.url);
  return (
    <div className="flex flex-col sm:flex-row gap-5 sm:gap-6 rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`seo-card-${col.id}`}>
      {col.image && (
        <div className="sm:w-52 lg:w-56 shrink-0">
          <img src={col.image} alt={col.title} loading="lazy" className="w-full h-44 sm:h-full object-cover rounded-xl" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        {col.eyebrow && <p className="text-[11px] font-bold tracking-[0.14em] uppercase text-primary mb-1.5">{col.eyebrow}</p>}
        <h3 className="font-display text-lg sm:text-xl font-bold text-slate-900 leading-snug">{col.title}</h3>
        {col.description && <p className="text-sm text-slate-500 mt-2 leading-relaxed">{col.description}</p>}
        <div className="mt-3">
          {links.map((l) => <RowLink key={l.id} link={l} />)}
        </div>
      </div>
    </div>
  );
}

export default function SeoFooter() {
  const cfg = useSiteConfig();
  const data = cfg.seo_footer && Object.keys(cfg.seo_footer).length ? cfg.seo_footer : DEFAULT_SEO_FOOTER;
  if (data.enabled === false) return null;
  const cols = (data.columns || []).filter((c) => c.enabled !== false && c.title);
  if (!cols.length) return null;
  return (
    <section className="bg-gradient-to-b from-slate-50 to-white border-t border-slate-100" data-testid="seo-footer-section">
      <div className="container-cz py-14 sm:py-16">
        <div className="text-center max-w-2xl mx-auto mb-9 sm:mb-10">
          {data.eyebrow && <p className="text-xs font-bold tracking-[0.18em] uppercase text-primary mb-3">{data.eyebrow}</p>}
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">{data.heading}</h2>
          {data.subheading && <p className="text-slate-500 mt-3 text-base sm:text-lg">{data.subheading}</p>}
        </div>
        <div className="grid lg:grid-cols-2 gap-5 sm:gap-6">
          {cols.map((c) => <LocationCard key={c.id} col={c} />)}
        </div>
      </div>
    </section>
  );
}
